import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import * as analytics from 'analytics/Analytics';
import {assert} from 'Asserts';
import {NucleotideType} from 'biojs/BioConstants';
import {ANALYTICS_MODULE} from 'constants/AnalyticsConstants';
import {BaseTranscript, Transcript} from 'constants/GenomeConstants';
import {DNA_SEQUENCE_IMPORT_FLOW} from 'molbio/MolBioConstants';
import {FONT_PROXIMA_11} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import FormGroup from 'ui-library/bs/FormGroup';
import {ButtonSize} from 'ui-library/buttons/Button';
import Chip, {ChipSize} from 'ui-library/chips/Chip';
import {BaseChipList} from 'ui-library/chips/ChipList';
import {GRAY_DARKER, GRAY_LIGHT, WHITE} from 'ui-library/Colors';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import ControlledNumericInput from 'ui-library/inputs/ControlledNumericInput';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import TextInput from 'ui-library/inputs/TextInput';
import Menu from 'ui-library/menus/Menu';
import {MenuHeight, MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';
import Section from 'ui-library/menus/Section';
import SectionHeader from 'ui-library/menus/SectionHeader';
import ToggleGroup from 'ui-library/ToggleGroup';
import {geneToName, genomeToName, locationToName, transcriptToName} from 'util/GenomeUtil';
import {ellipsize} from 'util/NameUtil';

import {DBImportItemViewProps, sharedItemStyles} from './DBImportItems';
import {
  DBHandle,
  DBQueryResultVersion,
  EnsemblDBImportAccession,
  LiveEnsemblGenome,
  LiveEnsemblTranscript,
} from './ImportSequenceConstants';

enum SEQUENCE_TYPE {
  GENOMIC = 'genomic',
  CDNA = 'cdna',
}
enum SEQUENCE_EXPANSION_DIRECTION {
  UPSTREAM = 'upstream',
  DOWNSTREAM = 'downstream',
}
export const NO_TRANSCRIPT_OPTION = 'None';

function DBEnsemblItemView({
  sequence,
  updateImportOptions,
  sequenceImportOptions,
}: DBImportItemViewProps): JSX.Element {
  const {resultData, importOptions} = sequence;
  assert(
    [DBHandle.PRIVATE, DBHandle.ENSEMBL].includes(resultData.dbHandle),
    `Unexpected DB source when rendering Ensembl result: ${resultData.dbHandle}`
  );

  const accession = importOptions.accession as EnsemblDBImportAccession;
  const selectedVersion = resultData.versions![importOptions.selectedVersionIndex!];
  const selectedSpecies = selectedVersion.genome as LiveEnsemblGenome;
  const {gene} = selectedVersion;

  function handleSelectTranscript(transcript: string): void {
    let newTranscripts: Array<string>;
    let newName;
    if (transcript === NO_TRANSCRIPT_OPTION) {
      newTranscripts = [];
    } else if (accession.transcriptIds.includes(transcript)) {
      newTranscripts = accession.transcriptIds.filter((t) => t !== transcript);
    } else {
      newTranscripts = [...accession.transcriptIds, transcript];
    }

    if (newTranscripts.length === 1) {
      newName = baseTranscriptToName(getTranscriptByAccValue(newTranscripts[0])!);
    } else if (newTranscripts.length > 1) {
      newName = '';
    } else {
      newName = geneToName(gene);
    }

    analytics.track('sequence', 'change_transcript', {
      product: ANALYTICS_MODULE.MOLBIO,
      importFlow: DNA_SEQUENCE_IMPORT_FLOW.DB,
      transcripts: newTranscripts,
      geneID: gene.id ?? gene.ensemblId,
      liveEnsemblUI: true,
    });

    // Sequence must be imported as a genomic sequence if no transcripts are selected
    const asCDNA = (newTranscripts.length > 0 && importOptions.asCDNA) ?? false;
    updateImportOptions({name: newName, asCDNA, accession: {...accession, transcriptIds: newTranscripts}});
  }

  function handleSelectVersion(version: DBQueryResultVersion, idx: number): void {
    const defaultTranscript =
      version.transcripts.find((transcript) => transcript.default) ?? version.transcripts[0] ?? null;
    const transcriptValue =
      resultData.dbHandle === DBHandle.ENSEMBL ? defaultTranscript?.ensemblId : defaultTranscript?.id;

    updateImportOptions({
      asCDNA: false,
      name: defaultTranscript != null ? baseTranscriptToName(defaultTranscript) : geneToName(version.gene),
      selectedVersionIndex: idx,
      accession: {
        ...accession,
        transcriptIds: transcriptValue != null ? [transcriptValue] : [],
      },
    });
  }

  function handleChangeUpDownStreamBases(
    value: number | null,
    direction: SEQUENCE_EXPANSION_DIRECTION
  ): void {
    updateImportOptions({
      accession: {
        ...accession,
        [direction]: value ?? 0,
      },
    });
    analytics.track('sequence', 'change_up_down_stream_bases', {
      product: ANALYTICS_MODULE.MOLBIO,
      importFlow: DNA_SEQUENCE_IMPORT_FLOW.DB,
      direction,
      liveEnsemblUI: true,
    });
  }

  function getTranscriptByAccValue(transcriptValue: string): Transcript | LiveEnsemblTranscript | undefined {
    return selectedVersion.transcripts.find(
      (t: Transcript) => transcriptValue === t.id || transcriptValue === t.ensemblId
    );
  }

  function baseTranscriptToName(transcript: BaseTranscript): string {
    return transcriptToName({id: '', ...transcript, ccdsId: null, refseqId: ''});
  }

  function getTranscriptDropdownValue(): Array<string> | string {
    if (resultData.dbHandle === DBHandle.ENSEMBL) {
      return accession.transcriptIds.length === 0 ? [NO_TRANSCRIPT_OPTION] : accession.transcriptIds;
    } else {
      return accession.transcriptIds.length === 0
        ? NO_TRANSCRIPT_OPTION
        : getTranscriptByAccValue(accession.transcriptIds[0])!.id!;
    }
  }

  function getSelectedTranscriptChips(): Array<JSX.Element> {
    return accession.transcriptIds.length > 0
      ? accession.transcriptIds.map((id) => {
          const transcript = getTranscriptByAccValue(id);
          return (
            <Chip
              key={id}
              className={css(styles.transcriptChip)}
              size={ChipSize.TABLE_CELL}
              text={ellipsize(baseTranscriptToName(transcript!), 15)}
              onRemove={() => {
                handleSelectTranscript(id);
              }}
            />
          );
        })
      : [<Chip key={gene.ensemblId} size={ChipSize.TABLE_CELL} text={geneToName(gene)} />];
  }

  return (
    <div
      className={css(sharedItemStyles.itemFlexContainer, sharedItemStyles.itemContainer)}
      {...getTestProps({component: 'DBImportItem', element: 'item', key: 'result'})}
    >
      <div className={css(styles.leftColumn)}>
        <div className={classNames(css(sharedItemStyles.dbImageColumn), 'media-object')}>
          {resultData.dbImg && <img className={css(sharedItemStyles.dbImageColumn)} src={resultData.dbImg} />}
          {resultData.dbHandle === DBHandle.ENSEMBL && (
            <p>
              {'This search is made possible thanks to work by '}
              <a
                href="http://europepmc.org/search/?query=DOI:10.1093/bioinformatics/btu613"
                target="_blank"
                rel="noopener noreferrer"
              >
                {'Yates et. al. '}
              </a>
              {'and '}
              <a href="https://doi.org/10.1093/nar/gkx1098" target="_blank" rel="noopener noreferrer">
                Zerbino et. al.
              </a>
            </p>
          )}
        </div>

        <div className={css(styles.infoColumn)}>
          <FieldLabel label="Gene" />
          <p className={css(sharedItemStyles.itemInfoText)}>{geneToName(gene)}</p>

          <FormGroup>
            <FieldLabel label="Species" />
            {resultData.versions!.length === 1 ? (
              <p className={css(sharedItemStyles.itemInfoText)}>
                {genomeToName({id: 0, ...selectedVersion.genome})}
              </p>
            ) : (
              <SelectDropdown
                className={css(styles.speciesDropdown)}
                alwaysDropDown={true}
                displayName={geneToName(gene)}
                menu={
                  <Menu height={MenuHeight.MEDIUM} width={MenuWidth.FULL} scrollable={true}>
                    {resultData.versions!.map((version, idx) => (
                      <Option
                        key={version.genome.organismName}
                        value={version}
                        onSelect={(version: DBQueryResultVersion) => {
                          handleSelectVersion(version, idx);
                        }}
                        isSelected={importOptions.selectedVersionIndex === idx}
                      >
                        {genomeToName({id: 0, ...version.genome})}
                      </Option>
                    ))}
                  </Menu>
                }
              />
            )}
          </FormGroup>

          {selectedSpecies.ensemblVersion && (
            <>
              <FieldLabel label="Ensembl version" />
              <p className={css(sharedItemStyles.itemInfoText, styles.withHelpTextMargin)}>
                {selectedSpecies.ensemblVersion}
              </p>
              <p className={css(styles.helpText)}>
                We'll import your data from the most recent Ensembl version.
              </p>
            </>
          )}

          <FieldLabel label="Location" />
          <p className={css(sharedItemStyles.itemInfoText, styles.withHelpTextMargin)}>
            {locationToName({length: 0, ...selectedVersion.chromosome}, gene.start, gene.end, gene.strand)}
          </p>
          {gene.strand === -1 && (
            <CheckboxInput
              id="db-import-revcomp"
              checked={importOptions.revcomp!}
              label="Import in sense orientation"
              onChange={(e) => {
                updateImportOptions({revcomp: e.target.checked});
              }}
              disabled={sequenceImportOptions.nucleotideType === NucleotideType.RNA && importOptions.asCDNA}
            />
          )}
        </div>
      </div>

      <div>
        <FormGroup>
          <FieldLabel label="Name" />
          <TextInput
            value={importOptions.name}
            onChange={(e) => {
              updateImportOptions({name: e.target.value});
            }}
            disabled={accession.transcriptIds.length > 1}
          />
          {accession.transcriptIds.length > 1 && (
            <p className={css(styles.helpText)}>
              You're about to import multiple transcripts. We'll default to transcript name for each.
            </p>
          )}
        </FormGroup>

        <FormGroup>
          <FieldLabel label="Transcript" />
          <Dropdown
            closeOnInsideClick={false}
            alwaysDropDown={true}
            menu={
              <Menu
                style={{height: '300px'}}
                scrollable={true}
                height={MenuHeight.MEDIUM}
                width={MenuWidth.FULL}
                isMulti={resultData.dbHandle === DBHandle.ENSEMBL}
                value={getTranscriptDropdownValue()}
              >
                <Section>
                  {selectedVersion.transcripts.length > 0 && <SectionHeader>Transcript</SectionHeader>}
                  {selectedVersion.transcripts
                    .sort((transcript, _) => (transcript.default ? -1 : 0))
                    .map((transcript) => {
                      const transcriptValue =
                        resultData.dbHandle === DBHandle.PRIVATE ? transcript.id : transcript.ensemblId;
                      const biotype = (transcript as LiveEnsemblTranscript).biotype ?? '';
                      const isCanonical = (transcript as LiveEnsemblTranscript).isCanonical ?? false;
                      return (
                        <Option
                          key={transcriptValue}
                          value={transcriptValue}
                          onSelect={handleSelectTranscript}
                        >
                          <span className={css(styles.transcriptName)}>
                            {transcriptToName({id: '', ...transcript, ccdsId: null, refseqId: ''})}
                          </span>
                          {(biotype.length > 0 || isCanonical) && (
                            <span className="faded">{`${isCanonical ? 'Canonical / ' : ''}${biotype}`}</span>
                          )}
                        </Option>
                      );
                    })}
                </Section>
                <Section>
                  <SectionHeader>Genomic DNA</SectionHeader>
                  <Option
                    key={NO_TRANSCRIPT_OPTION}
                    value={NO_TRANSCRIPT_OPTION}
                    onSelect={handleSelectTranscript}
                    skipIconOverride={true}
                  >
                    {geneToName(gene)}
                  </Option>
                </Section>
              </Menu>
            }
          >
            <div className={css(sharedItemStyles.itemInfoText, styles.selectedTranscripts)}>
              <BaseChipList
                chips={getSelectedTranscriptChips()}
                maxChips={3}
                isVertical={false}
                chipSize={ChipSize.TABLE_CELL}
                showMoreSize={ChipSize.TABLE_CELL}
                showLessSize={ButtonSize.MINI}
              />
            </div>
          </Dropdown>
        </FormGroup>

        <FormGroup>
          <FieldLabel label="Import as" />
          <ToggleGroup
            options={[
              {id: SEQUENCE_TYPE.GENOMIC, buttonProps: {text: 'Genomic sequence'}},
              {id: SEQUENCE_TYPE.CDNA, buttonProps: {text: 'cDNA'}},
            ]}
            selection={importOptions.asCDNA ? 'cdna' : 'genomic'}
            onChangeSelection={(selection) => {
              const asCDNA = selection === SEQUENCE_TYPE.CDNA;
              // cDNA sequences cannot have upstream or downstream bases specified
              updateImportOptions({
                asCDNA,
                accession: {...accession, ...(asCDNA && {upstream: 0, downstream: 0})},
              });
            }}
            size={ButtonSize.SMALL}
            isFullWidth={true}
            disabled={accession.transcriptIds.length === 0}
          />
          <p className={css(styles.helpText)}>
            {`You're about to import a ${
              importOptions.asCDNA ? 'cDNA sequence (exons only)' : 'genomic sequence'
            } with transcript-specific annotations.`}
          </p>
        </FormGroup>

        <div className={css(sharedItemStyles.itemFlexContainer)}>
          <FormGroup
            className={css(sharedItemStyles.fullWidthColumn, styles.upstreamInput, styles.withHelpTextMargin)}
          >
            <FieldLabel label="Upstream bases" />
            <ControlledNumericInput
              min={0}
              value={importOptions.asCDNA ? 0 : accession.upstream}
              onChange={(e) => {
                handleChangeUpDownStreamBases(e.target.numericValue, SEQUENCE_EXPANSION_DIRECTION.UPSTREAM);
              }}
              required={true}
              isDisabled={importOptions.asCDNA}
            />
          </FormGroup>
          <FormGroup className={css(sharedItemStyles.fullWidthColumn, styles.withHelpTextMargin)}>
            <FieldLabel label="Downstream bases" />
            <ControlledNumericInput
              min={0}
              value={importOptions.asCDNA ? 0 : accession.downstream}
              onChange={(e) => {
                handleChangeUpDownStreamBases(e.target.numericValue, SEQUENCE_EXPANSION_DIRECTION.DOWNSTREAM);
              }}
              required={true}
              isDisabled={importOptions.asCDNA}
            />
          </FormGroup>
        </div>
        {importOptions.asCDNA && (
          <p className={css(styles.helpText)}>
            You cannot import upstream or downstream bases for non-genomic sequences.
          </p>
        )}
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  upstreamInput: {
    marginRight: '16px',
  },
  helpText: {
    ...FONT_PROXIMA_11,
    color: GRAY_DARKER,
  },
  withHelpTextMargin: {
    marginBottom: '6px',
  },
  transcriptName: {
    display: 'block',
  },
  leftColumn: {
    minWidth: '50%',
    display: 'flex',
    marginRight: 16,
  },
  infoColumn: {
    flex: 1,
  },
  speciesDropdown: {
    marginBottom: '16px',
  },
  selectedTranscripts: {
    backgroundColor: WHITE,
    border: `1px solid ${GRAY_LIGHT}`,
    cursor: 'pointer',
  },
  transcriptChip: {
    marginTop: '1px',
    marginBottom: '1px',
  },
});

export default DBEnsemblItemView;
